import React, { useState } from 'react';
import { X, AlertTriangle, User, MessageSquare, FileText, CheckCircle2 } from 'lucide-react';
import { reportService, ReportReason } from '../../services/reportService';

interface ReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onReport?: (reason: string, description: string) => Promise<void>; // Made optional
  targetType: 'USER' | 'POST' | 'COMMENT';
  targetId: number;
  targetName?: string;
}

const REPORT_REASONS = [
  { value: 'SPAM', label: 'Spam', description: 'Nội dung spam hoặc quảng cáo không mong muốn' },
  { value: 'HARASSMENT', label: 'Quấy rối', description: 'Hành vi quấy rối hoặc đe dọa' },
  { value: 'HATE_SPEECH', label: 'Ngôn từ thù địch', description: 'Ngôn từ thù địch hoặc phân biệt đối xử' },
  { value: 'VIOLENCE', label: 'Bạo lực', description: 'Nội dung bạo lực hoặc đe dọa' },
  { value: 'NUDITY', label: 'Nội dung không phù hợp', description: 'Nội dung không phù hợp hoặc khôa thân' },
  { value: 'FALSE_INFORMATION', label: 'Thông tin sai sự thật', description: 'Tin giả hoặc thông tin sai lệch' },
  { value: 'ILLEGAL_CONTENT', label: 'Nội dung bất hợp pháp', description: 'Nội dung vi phạm pháp luật' },
  { value: 'IMPERSONATION', label: 'Mạo danh', description: 'Mạo danh người khác' },
  { value: 'SELF_HARM', label: 'Tự gây tổn hại', description: 'Nội dung liên quan đến tự tử hoặc tự gây thương tích' },
  { value: 'INTELLECTUAL_PROPERTY', label: 'Vi phạm bản quyền', description: 'Xâm phạm sở hữu trí tuệ' },
  { value: 'OTHER', label: 'Khác', description: 'Lý do khác' }
];

export const ReportModal: React.FC<ReportModalProps> = ({
  isOpen,
  onClose,
  onReport,
  targetType,
  targetId,
  targetName
}) => {
  const [selectedReason, setSelectedReason] = useState('');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedReason || !description.trim()) {
      setError('Vui lòng chọn lý do và nhập mô tả');
      return;
    }

    if (description.length > 1000) {
      setError('Mô tả không được vượt quá 1000 ký tự');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Use reportService if onReport callback is not provided
      if (onReport) {
        await onReport(selectedReason, description.trim());
      } else {
        await reportService.reportPost({
          targetType,
          targetId,
          reason: selectedReason as ReportReason,
          description: description.trim()
        });
      }
      
      setIsSuccess(true);

      // Auto close after 2 seconds
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (error: any) {
      console.error('Report submission error:', error);
      
      if (error.response?.data?.message) {
        setError(error.response.data.message);
      } else if (error.message?.includes('Cannot report your own')) {
        setError('Bạn không thể báo cáo nội dung của chính mình');
      } else {
        setError('Không thể gửi báo cáo. Vui lòng thử lại sau.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setSelectedReason('');
      setDescription('');
      setIsSuccess(false);
      setError(null);
      onClose();
    }
  };

  const getTargetIcon = () => {
    switch (targetType) {
      case 'USER':
        return <User className="w-5 h-5" />;
      case 'POST':
        return <FileText className="w-5 h-5" />;
      case 'COMMENT':
        return <MessageSquare className="w-5 h-5" />;
      default:
        return <AlertTriangle className="w-5 h-5" />;
    }
  };

  const getTargetLabel = () => {
    switch (targetType) {
      case 'USER':
        return 'người dùng';
      case 'POST':
        return 'bài viết';
      case 'COMMENT':
        return 'bình luận';
      default:
        return 'nội dung';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-red-100 rounded-full">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Báo cáo {getTargetLabel()}</h2>
              <p className="text-sm text-gray-600">
                Báo cáo {targetName ? `"${targetName}"` : `ID: ${targetId}`}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Success State */}
        {isSuccess ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Báo cáo đã được gửi</h3>
            <p className="text-gray-600">
              Cảm ơn bạn đã giúp giữ cho cộng đồng an toàn. 
              Chúng tôi sẽ xem xét báo cáo của bạn trong thời gian sớm nhất.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-6">\
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Lý do báo cáo
            </label>
            <div className="space-y-2">
              {REPORT_REASONS.map((reason) => (
                <label
                  key={reason.value}
                  className="flex items-start space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <input
                    type="radio"
                    name="reason"
                    value={reason.value}
                    checked={selectedReason === reason.value}
                    onChange={(e) => setSelectedReason(e.target.value)}
                    className="mt-1 text-red-600 focus:ring-red-500"
                  />
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">{reason.label}</div>
                    <div className="text-sm text-gray-600">{reason.description}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Mô tả chi tiết
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Vui lòng mô tả chi tiết lý do báo cáo..."
              disabled={isSubmitting}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none disabled:opacity-50 disabled:bg-gray-100"
              rows={4}
              maxLength={1000}
              required
            />
            <div className="flex justify-between items-center mt-1">
              <p className="text-xs text-gray-500">
                Mô tả chi tiết sẽ giúp chúng tôi xử lý báo cáo hiệu quả hơn
              </p>
              <p className="text-xs text-gray-500">
                {description.length}/1000 ký tự
              </p>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
            >
              Hủy
            </button>
            <button
              type="submit"
              disabled={!selectedReason || !description.trim() || isSubmitting}
              className="flex-1 px-4 py-2 bg-red-600 text-white hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed rounded-lg transition-colors"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2 inline-block" />
                  Đang gửi...
                </>
              ) : (
                'Gửi báo cáo'
              )}
            </button>
          </div>
        </form>
        )}
      </div>
    </div>
  );
};
